import { PlugIcon } from "lucide-react";
import { useEffect, useState } from "react";

import { cn } from "../../lib/utils";
import { getConnectorBrandSvg } from "./connector-brand/connector-brand-svgs";

export interface PraxaConnectorLogoProps {
  readonly className?: string;
  readonly fallbackSlug?: string | null;
  readonly logoUrl?: string | null;
  readonly name: string;
  readonly slug?: string | null;
}

export interface PraxaConnectorMonochromeLogoProps {
  readonly className?: string;
  readonly name: string;
  readonly slug?: string | null;
}

function safeConnectorLogoUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === "https:" ? url.href : null;
  } catch {
    return null;
  }
}

function SvgMark({
  className,
  svg,
}: {
  readonly className?: string | undefined;
  readonly svg: string;
}) {
  return (
    <img
      alt=""
      className={cn("size-full object-contain", className)}
      decoding="async"
      draggable={false}
      loading="lazy"
      // A data URL keeps every bundled SVG in its own image document. That avoids
      // collisions between common internal ids such as "a" across catalog rows.
      src={`data:image/svg+xml,${encodeURIComponent(svg)}`}
    />
  );
}

export function PraxaConnectorLogo({
  className,
  fallbackSlug,
  logoUrl,
  name,
  slug,
}: PraxaConnectorLogoProps) {
  const [remoteFailed, setRemoteFailed] = useState(false);
  const lightSvg =
    getConnectorBrandSvg(slug, false, name) ?? getConnectorBrandSvg(fallbackSlug, false);
  const darkSvg =
    getConnectorBrandSvg(slug, true, name) ?? getConnectorBrandSvg(fallbackSlug, true);
  const remoteUrl = safeConnectorLogoUrl(logoUrl);

  useEffect(() => {
    setRemoteFailed(false);
  }, [remoteUrl]);

  if (lightSvg) {
    const hasThemePair = darkSvg !== undefined && darkSvg !== lightSvg;
    return (
      <span
        aria-hidden
        className={cn("flex size-4.5 shrink-0 items-center justify-center", className)}
        data-logo-source="thesvg"
        data-logo-theme={hasThemePair ? "light-dark" : "shared"}
      >
        <SvgMark className={hasThemePair ? "dark:hidden" : undefined} svg={lightSvg} />
        {hasThemePair ? <SvgMark className="hidden dark:block" svg={darkSvg ?? lightSvg} /> : null}
      </span>
    );
  }

  if (remoteUrl && !remoteFailed) {
    return (
      <span
        aria-hidden
        className={cn(
          "flex size-4.5 shrink-0 items-center justify-center rounded-[3px] bg-[#b8b8b8] p-0.5",
          className,
        )}
        data-logo-source="catalog"
        data-logo-theme="contrast-tile"
      >
        <img
          alt=""
          className="size-full object-contain"
          decoding="async"
          draggable={false}
          loading="lazy"
          onError={() => setRemoteFailed(true)}
          referrerPolicy="no-referrer"
          src={remoteUrl}
        />
      </span>
    );
  }

  return (
    <PlugIcon
      aria-hidden
      className={cn("size-4.5 shrink-0 text-muted-foreground", className)}
      data-logo-source="fallback"
    />
  );
}

export function PraxaConnectorMonochromeLogo({
  className,
  name,
  slug,
}: PraxaConnectorMonochromeLogoProps) {
  const svg = getConnectorBrandSvg(slug, false, name);

  if (!svg) {
    return (
      <PlugIcon
        aria-hidden
        className={cn("size-4 shrink-0", className)}
        data-logo-source="fallback"
      />
    );
  }

  const maskImage = `url(data:image/svg+xml,${encodeURIComponent(svg)})`;
  return (
    <span
      aria-hidden
      className={cn("block size-4 shrink-0", className)}
      data-logo-source="thesvg-monochrome"
      style={{
        backgroundColor: "currentColor",
        maskImage,
        maskPosition: "center",
        maskRepeat: "no-repeat",
        maskSize: "contain",
        WebkitMaskImage: maskImage,
        WebkitMaskPosition: "center",
        WebkitMaskRepeat: "no-repeat",
        WebkitMaskSize: "contain",
      }}
    />
  );
}
